'use client';

import React from 'react';
import SEOHeading from '@/components/common/SEOHeading';
import AppImage from '@/components/ui/AppImage';
import { trackCTAClick } from '@/lib/seo/conversionTracking';

interface HeroSectionProps {
  onScrollToForm: () => void;
}

const HeroSection = ({ onScrollToForm }: HeroSectionProps) => {
  const handleCTAClick = () => {
    trackCTAClick('hero_section', 'Reserve Your Puppy Today');
    onScrollToForm();
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-16 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-4 py-2 animate-pulse">
              <span className="h-2 w-2 animate-pulse rounded-full bg-accent"></span>
              <span className="caption font-semibold text-accent">
                🔥 8 Puppies Left - They're Going Fast!
              </span>
            </div>

            <SEOHeading level={1} className="text-balance">
              Find Your Perfect Labrador Puppy - English, Yellow, Black & Chocolate Labs
            </SEOHeading>

            <p className="text-lg text-muted-foreground lg:text-xl">
              Looking for a loyal companion? Our beautiful Labrador puppies are raised with love and care by experienced breeders who truly understand the breed. Every puppy comes from champion bloodlines, is AKC-certified, and includes a comprehensive 2-year health guarantee. Whether you're drawn to the classic English Lab, a sunny Yellow Lab, a sleek Black Lab, a sweet Chocolate Lab, or the stunning Fox Red variety, we'll help you find the perfect match for your family.
            </p>

            {/* Urgency Banner */}
            <div className="rounded-lg border-2 border-warning/30 bg-warning/5 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-warning/20">
                  <span className="text-xl">⏰</span>
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    This Weekend Only: Save $200 Plus Get a Complete Starter Kit
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Reserve your puppy before Sunday and receive a premium starter kit with everything you need (a $350 value, absolutely free!)
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleCTAClick}
                className="flex h-14 items-center justify-center gap-2 rounded-md bg-accent px-8 font-semibold text-accent-foreground shadow-warm-md transition-smooth hover:scale-[0.97] hover:shadow-warm-lg focus-ring">

                Reserve Your Puppy Today
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) =>
                  <svg
                    key={i}
                    className="h-5 w-5 text-warning"
                    fill="currentColor"
                    viewBox="0 0 20 20">

                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  )}
                </div>
                <span className="text-sm font-medium text-foreground">
                  Rated 4.9 out of 5 by over 200 families
                </span>
              </div>
              <div className="h-6 w-px bg-border"></div>
              <div className="text-sm font-medium text-foreground">
                ✓ Fully AKC Certified
              </div>
              <div className="h-6 w-px bg-border"></div>
              <div className="text-sm font-medium text-foreground">
                ✓ 2-Year Health Guarantee
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-warm-2xl">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_128d6d071-1769959685052.png"
                alt="Premium AKC-certified Yellow Labrador Retriever puppy for sale with champion bloodlines from reputable English Labrador breeders"
                width={600}
                height={450}
                priority={true}
                quality={90}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                className="h-full w-full rounded-xl object-cover shadow-warm-xl" />


            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -left-6 rounded-xl bg-card p-4 shadow-warm-lg">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                  <svg
                    className="h-6 w-6 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">

                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />

                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Complete Health Guarantee
                  </p>
                  <p className="caption text-muted-foreground">
                    Protected for 2 Full Years
                  </p>
                </div>
              </div>
            </div>
            {/* Urgency Badge */}
            <div className="absolute -top-4 -right-4 rounded-full bg-accent px-4 py-2 shadow-warm-lg animate-bounce">
              <p className="text-sm font-bold text-accent-foreground">
                3 Left!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;